import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Alert, Button, Input, Modal } from '@/components/ui';
import type { ApiError } from '@/lib/api/client';
import type { AddressOut } from '@/lib/api/types';
import { LocationPicker, type PickedLocation } from '@/features/location/LocationPicker';
import { addressSchema, type AddressFormValues } from '@/lib/validators';

export interface AddressEditorProps {
  open: boolean;
  onClose: () => void;
  /** The address being corrected. */
  address: AddressOut;
  onSave: (values: AddressFormValues, location: PickedLocation | null) => void;
  saving: boolean;
  error?: ApiError | null;
}

/**
 * Correct one saved address.
 *
 * A modal rather than a page, and edit-only rather than create-or-edit: a new
 * address is added during checkout, where the PIN code also has to price the
 * delivery. That is a different job with a different form
 * (features/address/AddressForm), and folding the two together would drag
 * delivery pricing into a screen that has no basket.
 *
 * The schema is the SAME `addressSchema` the checkout uses. A typo the checkout
 * would reject must not be savable here — otherwise the account page becomes a
 * way to put an unusable address in the book and only discover it at the next
 * order.
 */
export function AddressEditor({
  open,
  onClose,
  address,
  onSave,
  saving,
  error,
}: AddressEditorProps) {
  const formId = useId();
  // Seeded from the row, so an address that already has a pin opens with the
  // map on it rather than asking again. One saved before we started asking
  // opens empty — which is also what checkout would refuse it for, so this
  // modal is where that gets fixed.
  const [location, setLocation] = useState<PickedLocation | null>(() => toLocation(address));

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressSchema),
    mode: 'onBlur',
    defaultValues: toFormValues(address),
  });
  const { register, formState, handleSubmit, reset, setError } = form;

  // Re-seed whenever a different address is opened, or the same one is
  // reopened after a cancelled edit: without this the modal would still be
  // holding the previous row's values.
  useEffect(() => {
    if (open) {
      reset(toFormValues(address));
      setLocation(toLocation(address));
    }
  }, [open, address, reset]);

  // The server's 422 is the authority on fields the client cannot check —
  // a PIN code that is well-formed but not one we deliver to, say.
  useEffect(() => {
    if (!error) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      if (field in address || field === 'line2') {
        setError(field as keyof AddressFormValues, { type: 'server', message });
      }
    }
  }, [error, setError, address]);

  const errorFor = (field: keyof AddressFormValues): string | undefined =>
    formState.errors[field]?.message;

  // handleSubmit returns a promise; onSubmit wants void. Same shape the admin
  // editors use.
  const submit = handleSubmit((values) => onSave(values, location));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit address"
      footer={
        <>
          <Button type="submit" form={formId} loading={saving} fullWidth>
            Save changes
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={saving} fullWidth>
            Cancel
          </Button>
        </>
      }
    >
      {/* Only the messages the fields cannot carry themselves. A 422 with field
          errors is already shown against the offending input above. */}
      {error && Object.keys(error.fieldErrors).length === 0 ? (
        <Alert severity="danger" title="We could not save that">
          <p>{error.message}</p>
        </Alert>
      ) : null}

      <form
        id={formId}
        onSubmit={(event) => void submit(event)}
        className="flex flex-col gap-3"
        noValidate
      >
        <Input
          {...register('name')}
          label="Full name"
          error={errorFor('name')}
          autoComplete="name"
          required
        />
        <Input
          {...register('phone')}
          label="Mobile number"
          error={errorFor('phone')}
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          required
        />
        <Input
          {...register('line1')}
          label="Flat / house number and street"
          error={errorFor('line1')}
          autoComplete="address-line1"
          required
        />
        <Input
          {...register('line2')}
          label="Area or landmark (optional)"
          hint="Locality, and anything that helps find the door."
          error={errorFor('line2')}
          autoComplete="address-line2"
        />
        <Input
          {...register('city')}
          label="City or town"
          error={errorFor('city')}
          autoComplete="address-level2"
          required
        />
        <Input
          {...register('state')}
          label="State"
          error={errorFor('state')}
          autoComplete="address-level1"
          required
        />
        <Input
          {...register('pincode')}
          label="PIN code"
          error={errorFor('pincode')}
          inputMode="numeric"
          autoComplete="postal-code"
          required
        />

        <div className="border-t border-line pt-3">
          <LocationPicker value={location} onChange={setLocation} />
        </div>
      </form>
    </Modal>
  );
}

/**
 * The API models "no landmark" as null; the form models it as an empty string.
 * Passing null into an input makes it uncontrolled and React complains at the
 * moment the customer first types.
 */
/** The stored pin, if this address has one. Decimal strings on the wire. */
function toLocation(address: AddressOut): PickedLocation | null {
  if (address.latitude == null || address.longitude == null) return null;
  return {
    lat: Number(address.latitude),
    lng: Number(address.longitude),
    source: address.location_source === 'device' ? 'device' : 'map',
    accuracyM: address.location_accuracy_m ?? null,
  };
}

function toFormValues(address: AddressOut): AddressFormValues {
  return {
    name: address.name,
    phone: address.phone,
    line1: address.line1,
    // Area and landmark are one box now, so an address saved when they were
    // two is shown joined. Saving then writes the whole thing to line2 and
    // clears landmark (see SavedAddresses) — otherwise the landmark would
    // appear twice on the label, once in each field.
    line2: [address.line2, address.landmark].filter(Boolean).join(', '),
    city: address.city,
    state: address.state,
    pincode: address.pincode,
  };
}
